import Checkbox from '@material-ui/core/Checkbox';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Collapse, IconButton } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useCallback } from 'react';
import TableOmniNewAnimated, { CopyableCell, getComparator } from './TableOmniNewAnimated';
import PaginationActionsTableOmni from './PaginationActionsTableOmni';

const useStyles = makeStyles(theme => ({
	'@keyframes tableContainerEnter': {
		'0%': { opacity: 0 },
		'100%': { opacity: 1 }
	},
	'@keyframes rowEnter': {
		'0%': { opacity: 0, transform: 'translateY(6px)' },
		'100%': { opacity: 1, transform: 'translateY(0)' }
	},
	'@keyframes emptyFloat': {
		'0%': { transform: 'translateY(0)' },
		'50%': { transform: 'translateY(-10px)' },
		'100%': { transform: 'translateY(0)' }
	},
	paper: {
		backgroundColor: `${theme.palette.background.paper} !important`,
		maxWidth: '100vw',
		borderRadius: '16px !important',
		border: `1px solid ${theme.palette.divider}`,
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'hidden',
		animation: '$tableContainerEnter 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both',
		boxShadow:
			theme.palette.type === 'dark'
				? '0 6px 18px rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.25) !important'
				: '0 6px 18px rgba(15,23,42,0.07), 0 2px 6px rgba(15,23,42,0.04) !important'
	},
	tableContainer: {
		borderRadius: '16px',
		overflow: 'hidden',
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%'
	},
	tableWrapper: {
		overflow: 'auto',
		flex: 1,
		minHeight: 0,
		position: 'relative',
		width: '100%',
		WebkitOverflowScrolling: 'touch',
		'&::-webkit-scrollbar': { width: '8px', height: '8px' },
		'&::-webkit-scrollbar-track': { background: theme.palette.divider, borderRadius: '4px' },
		'&::-webkit-scrollbar-thumb': {
			background: theme.palette.action.disabled,
			borderRadius: '4px',
			'&:hover': { background: theme.palette.action.selected }
		}
	},
	table: {
		minWidth: '100%',
		fontSize: '18px !important',
		borderCollapse: 'separate',
		borderSpacing: 0
	},
	stickyHeader: {
		position: 'sticky',
		top: 0,
		zIndex: 5,
		backgroundColor: theme.palette.background.tableHeader
	},
	header: {
		backgroundColor: '#102239 !important',
		position: 'sticky',
		top: 0,
		zIndex: 10
	},
	tableCell: {
		whiteSpace: 'nowrap',
		padding: '16px 16px 16px 16px',
		borderBottom: `1px solid ${theme.palette.divider}`,
		transition: 'transform 0.3s ease, color 0.3s ease'
	},
	headerCell: {
		color: `${theme.palette.common.white} !important`,
		fontSize: '1.1rem !important',
		backgroundColor: `${theme.palette.background.tableHeader} !important`,
		fontWeight: 600,
		padding: '16px',
		borderBottom: 'none'
	},
	sortLabel: {
		color: `${theme.palette.common.white} !important`,
		'&:hover': { color: `${theme.palette.common.white} !important` },
		'&.Mui-active': { color: `${theme.palette.common.white} !important` },
		'& .MuiTableSortLabel-icon': {
			color: `${theme.palette.common.white} !important`,
			transition: 'transform 0.3s ease'
		}
	},
	tableRow: {
		opacity: 0,
		animation: '$rowEnter 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94) both',
		transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: `${theme.palette.action.hover} !important`,
			boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
		}
	},
	expandedRowCell: {
		padding: 0,
		borderBottom: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.015)'
	},
	innerWrapper: {
		padding: theme.spacing(2),
		// Keep the nested table's font as-is; just shorten its header row so it
		// reads as an inner sub-table rather than another full-height header.
		'& .MuiTableCell-head': {
			paddingTop: '6px !important',
			paddingBottom: '6px !important'
		}
	},
	pagination: {
		position: 'sticky',
		bottom: 0,
		backgroundColor: theme.palette.background.paper,
		zIndex: 5,
		flexShrink: 0,
		borderTop: `1px solid ${theme.palette.divider}`,
		padding: '8px 0'
	},
	checkbox: {
		color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff',
		'&.Mui-checked': { color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff' },
		'&.MuiCheckbox-indeterminate': {
			color: theme.palette.secondary?.value || theme.palette.secondary?.main || '#fff'
		}
	},
	expandIcon: {
		color: theme.palette.common.white,
		transition: 'transform 0.25s ease'
	},
	emptyState: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '400px',
		flexDirection: 'column',
		gap: '16px'
	},
	emptyIcon: {
		fontSize: '64px !important',
		color: theme.palette.text.disabled,
		animation: '$emptyFloat 2.2s ease-in-out infinite'
	},
	// ---- classes required by the shared CopyableCell renderer ----
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '4px',
		padding: '4px 12px',
		borderRadius: '16px',
		fontSize: '12px',
		fontWeight: 500,
		whiteSpace: 'nowrap'
	},
	pillIcon: {
		fontSize: '16px !important'
	},
	copyButton: {
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		padding: '4px 8px',
		borderRadius: '4px',
		color: 'inherit',
		fontSize: 'inherit',
		textAlign: 'left',
		'&:hover': { backgroundColor: theme.palette.action.hover }
	},
	viewText: {
		color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280',
		fontWeight: 500,
		fontSize: '1.2rem',
		cursor: 'pointer',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		'&:hover': {
			color: theme.palette.type === 'dark' ? '#D1D5DB' : '#374151',
			textDecoration: 'underline'
		}
	},
	detailsText: {
		color: theme.palette.type === 'dark' ? '#9CA3AF' : '#6B7280',
		fontWeight: 500,
		fontSize: '1.2rem',
		cursor: 'pointer',
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		'&:hover': {
			color: theme.palette.type === 'dark' ? '#D1D5DB' : '#374151',
			textDecoration: 'underline'
		}
	}
}));

function OuterTableHead({
	classes,
	theme,
	headCells,
	order,
	orderBy,
	isSorted,
	onRequestSort,
	checkBox,
	numSelected,
	rowCount,
	onSelectAllClick
}) {
	const createSortHandler = property => event => onRequestSort(event, property);

	return (
		<TableHead className={classes.stickyHeader}>
			<TableRow className={classes.header}>
				{/* Expand toggle column */}
				<TableCell
					style={{ backgroundColor: theme.palette.background.tableHeader, width: 48 }}
					padding="checkbox"
					className={classes.headerCell}
				/>
				{checkBox && (
					<TableCell
						style={{ backgroundColor: theme.palette.background.tableHeader }}
						padding="checkbox"
						className={classes.headerCell}
					>
						<Checkbox
							indeterminate={numSelected > 0 && numSelected < rowCount}
							checked={rowCount > 0 && numSelected === rowCount}
							onChange={onSelectAllClick}
							inputProps={{ 'aria-label': 'select all' }}
							className={classes.checkbox}
							style={{ color: theme.palette.common.white }}
						/>
					</TableCell>
				)}
				{headCells.map(headCell => (
					<TableCell
						classes={{ root: classes.tableCell }}
						key={headCell.id}
						align={headCell.numeric ? 'center' : 'left'}
						padding={headCell.disablePadding ? 'none' : 'normal'}
						sortDirection={isSorted && orderBy === headCell.id ? order : false}
						className={classes.headerCell}
					>
						{headCell.disableSort ? (
							<span>{headCell.label}</span>
						) : (
							<TableSortLabel
								active={isSorted && orderBy === headCell.id}
								direction={orderBy === headCell.id ? order : 'asc'}
								onClick={createSortHandler(headCell.id)}
								className={classes.sortLabel}
							>
								{headCell.label}
							</TableSortLabel>
						)}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

function OuterSkeleton({ classes, theme, headCells, checkBox, rowsPerPage = 5 }) {
	const skeletonRows = rowsPerPage > 0 ? rowsPerPage : 5;
	return (
		<div className={classes.tableWrapper}>
			<Table className={classes.table} size="medium" stickyHeader>
				<TableHead className={classes.stickyHeader}>
					<TableRow className={classes.header}>
						<TableCell padding="checkbox" className={classes.headerCell} />
						{checkBox && <TableCell padding="checkbox" className={classes.headerCell} />}
						{headCells.map(headCell => (
							<TableCell key={headCell.id} className={classes.headerCell}>
								<Skeleton variant="text" width="80%" height={24} animation="wave" />
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{[...Array(skeletonRows)].map((_, rowIndex) => (
						<TableRow key={rowIndex}>
							<TableCell padding="checkbox">
								<Skeleton variant="circle" width={24} height={24} animation="wave" />
							</TableCell>
							{checkBox && (
								<TableCell padding="checkbox">
									<Skeleton variant="rect" width={24} height={24} animation="wave" />
								</TableCell>
							)}
							{headCells.map((cell, cellIndex) => (
								<TableCell key={cellIndex} align={cell.numeric ? 'center' : 'left'}>
									<Skeleton variant="text" width="90%" height={20} animation="wave" />
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

/**
 * TableOmniExpandable
 *
 * A grouped/accordion table built on the same visual language and cell renderer
 * (`CopyableCell`) as `TableOmniNewAnimated`. Each outer row represents a group;
 * clicking the row (or its chevron) expands an inner `TableOmniNewAnimated` that
 * carries the full feature set (pills, dt formatting, tooltips, sorting, per-row
 * checkboxes, pagination).
 *
 * Selection is lifted to the parent as a flat array of inner-row keys
 * (`selectedInner` / `setSelectedInner`). The outer group checkbox and the inner
 * table's own select-all stay in sync because both derive from that single list.
 *
 * @param {Array}    rows              Outer group rows.
 * @param {Array}    headCells         Outer column configs (same shape as TableOmniNewAnimated).
 * @param {Array}    innerHeadCells    Inner column configs.
 * @param {Function} getInnerRows      (outerRow) => inner rows array. Default: row.campaigns.
 * @param {String|Function} outerRowKey  Key accessor for outer rows. Default: 'id'.
 * @param {String|Function} innerRowKey  Key accessor for inner rows. Default: 'id'.
 * @param {Boolean}  checkBox          Enable outer group checkbox + inner row checkboxes.
 * @param {Array}    selectedInner     Flat array of selected inner-row keys (controlled).
 * @param {Function} setSelectedInner  Setter for selectedInner (supports functional updates).
 * @param {Function} getInnerTitle     Optional (outerRow) => title for the inner table toolbar.
 */
function TableOmniExpandable({
	rows = [],
	headCells,
	innerHeadCells,
	getInnerRows = row => row.campaigns || [],
	outerRowKey = 'id',
	innerRowKey = 'id',
	checkBox = false,
	selectedInner = [],
	setSelectedInner,
	getInnerTitle,
	onEditClick,
	onViewClick,
	onDeleteClick,
	onDownloadClick,
	onDetailsClick,
	height = '100%',
	perPageRows = 10,
	rowsPerPageOptions = [5, 10, 25, 50],
	loading = false,
	emptyMessage = 'No Data Found!',
	fixedHeight = false,
	maxTableHeight = '600px',
	innerPerPageRows = 5,
	innerRowsPerPageOptions = [5, 10, 25],
	defaultExpandFirst = false
}) {
	const classes = useStyles();
	const theme = useTheme();

	const [order, setOrder] = React.useState('desc');
	const [orderBy, setOrderBy] = React.useState(headCells[0].id);
	const [isSorted, setIsSorted] = React.useState(false);
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(perPageRows || 10);
	const [expandedKeys, setExpandedKeys] = React.useState([]);
	const [showEmptyState, setShowEmptyState] = React.useState(false);

	const getOuterKey = useCallback(
		row => {
			if (typeof outerRowKey === 'function') return outerRowKey(row);
			return row[outerRowKey];
		},
		[outerRowKey]
	);

	const getInnerKey = useCallback(
		row => {
			if (typeof innerRowKey === 'function') return innerRowKey(row);
			return row[innerRowKey];
		},
		[innerRowKey]
	);

	// Keys of every inner row across every outer group (used for select-all + counts).
	const allInnerKeys = React.useMemo(() => {
		const keys = [];
		rows.forEach(r => getInnerRows(r).forEach(ir => keys.push(getInnerKey(ir))));
		return keys;
	}, [rows, getInnerRows, getInnerKey]);

	const numSelected = React.useMemo(
		() => allInnerKeys.filter(k => selectedInner.includes(k)).length,
		[allInnerKeys, selectedInner]
	);

	React.useEffect(() => {
		if (!loading && rows.length === 0) {
			const timer = setTimeout(() => setShowEmptyState(true), 300);
			return () => clearTimeout(timer);
		}
		setShowEmptyState(false);
		return undefined;
	}, [loading, rows.length]);

	React.useEffect(() => {
		if (defaultExpandFirst && rows.length > 0) {
			setExpandedKeys([getOuterKey(rows[0])]);
		}
	}, [rows, defaultExpandFirst, getOuterKey]);

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
		setIsSorted(true);
	};

	const handleChangePage = (event, newPage) => {
		if (rowsPerPage === -1) return;
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const toggleExpand = key => {
		setExpandedKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
	};

	// ---- Selection helpers ----
	const handleSelectAllClick = event => {
		if (!setSelectedInner) return;
		if (event.target.checked) {
			setSelectedInner(prev => Array.from(new Set([...prev, ...allInnerKeys])));
		} else {
			setSelectedInner(prev => prev.filter(k => !allInnerKeys.includes(k)));
		}
	};

	const setGroupSelected = (groupKeys, nextArr) => {
		if (!setSelectedInner) return;
		setSelectedInner(prev => [...prev.filter(k => !groupKeys.includes(k)), ...nextArr]);
	};

	const toggleGroup = (groupKeys, checked) => {
		if (!setSelectedInner) return;
		if (checked) {
			setSelectedInner(prev => Array.from(new Set([...prev, ...groupKeys])));
		} else {
			setSelectedInner(prev => prev.filter(k => !groupKeys.includes(k)));
		}
	};

	const visibleRows = React.useMemo(() => {
		const sorted = isSorted ? [...rows].sort(getComparator(order, orderBy)) : rows;
		if (rowsPerPage === -1) return sorted;
		return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
	}, [rows, isSorted, order, orderBy, page, rowsPerPage]);

	const colSpan = 1 + (checkBox ? 1 : 0) + headCells.length;

	const getValue = (row, id, cellConfig) => {
		if (cellConfig?.customRow) {
			return cellConfig.customRow(row);
		}
		const keys = id.split('.');
		const value = keys.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : ''), row);

		if (id === 'view') {
			return (
				<span
					className={classes.viewText}
					onClick={e => {
						e.stopPropagation();
						if (onViewClick) onViewClick(row);
					}}
				>
					{cellConfig?.label || 'VIEW'}
				</span>
			);
		}
		if (id === 'edit') {
			return (
				<EditIcon
					style={{ cursor: 'pointer' }}
					fontSize="large"
					onClick={e => {
						e.stopPropagation();
						if (onEditClick) onEditClick(row);
					}}
				/>
			);
		}
		if (id === 'delete') {
			return (
				<DeleteIcon
					style={{ cursor: 'pointer' }}
					fontSize="large"
					onClick={e => {
						e.stopPropagation();
						if (onDeleteClick) onDeleteClick(row);
					}}
				/>
			);
		}
		if (id === 'download') {
			return (
				<GetAppIcon
					style={{ cursor: 'pointer' }}
					fontSize="large"
					onClick={e => {
						e.stopPropagation();
						if (onDownloadClick) onDownloadClick(row);
					}}
				/>
			);
		}
		if (id === 'details') {
			return (
				<span
					className={classes.detailsText}
					onClick={e => {
						e.stopPropagation();
						if (onDetailsClick) onDetailsClick(row);
					}}
				>
					{cellConfig?.label || 'DETAILS'}
				</span>
			);
		}
		return value;
	};

	if (showEmptyState) {
		return (
			<Box sx={{ width: '100%', height }} className={classes.tableContainer}>
				<Paper className={classes.paper} sx={{ width: '100%', mb: 2, boxShadow: 'none' }}>
					<div className={classes.emptyState}>
						<SearchOffOutlinedIcon className={classes.emptyIcon} />
						<Typography
							style={{
								color: theme.palette.type === 'dark' ? '#FFFFFF' : theme.palette.text.primary,
								fontSize: 16,
								fontWeight: 500
							}}
						>
							{emptyMessage || 'No Data Found'}
						</Typography>
					</div>
				</Paper>
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%', height }} className={classes.tableContainer}>
			<Paper className={classes.paper} sx={{ width: '100%', mb: 0, boxShadow: 'none' }}>
				{loading || rows.length === 0 ? (
					<OuterSkeleton
						classes={classes}
						theme={theme}
						headCells={headCells}
						checkBox={checkBox}
						rowsPerPage={rowsPerPage > 0 ? rowsPerPage : 5}
					/>
				) : (
					<>
						<div
							className={classes.tableWrapper}
							style={fixedHeight ? { maxHeight: maxTableHeight, overflow: 'auto' } : {}}
						>
							<Table className={classes.table} size="medium" stickyHeader>
								<OuterTableHead
									classes={classes}
									theme={theme}
									headCells={headCells}
									order={order}
									orderBy={orderBy}
									isSorted={isSorted}
									onRequestSort={handleRequestSort}
									checkBox={checkBox}
									numSelected={numSelected}
									rowCount={allInnerKeys.length}
									onSelectAllClick={handleSelectAllClick}
								/>
								<TableBody>
									{visibleRows.map((row, index) => {
										const outerKey = getOuterKey(row);
										const innerRows = getInnerRows(row);
										const groupKeys = innerRows.map(getInnerKey);
										const isExpanded = expandedKeys.includes(outerKey);
										const groupSelectedCount = groupKeys.filter(k =>
											selectedInner.includes(k)
										).length;
										const allGroupSelected =
											groupKeys.length > 0 && groupSelectedCount === groupKeys.length;
										const someGroupSelected = groupSelectedCount > 0 && !allGroupSelected;
										const staggerDelay = `${Math.min(index, 15) * 25}ms`;

										return (
											<React.Fragment key={outerKey}>
												<TableRow
													hover
													className={classes.tableRow}
													style={{ animationDelay: staggerDelay }}
													onClick={() => toggleExpand(outerKey)}
												>
													<TableCell padding="checkbox" className={classes.tableCell}>
														<IconButton
															size="small"
															onClick={e => {
																e.stopPropagation();
																toggleExpand(outerKey);
															}}
														>
															{isExpanded ? (
																<KeyboardArrowDownIcon />
															) : (
																<KeyboardArrowRightIcon />
															)}
														</IconButton>
													</TableCell>
													{checkBox && (
														<TableCell
															padding="checkbox"
															className={classes.tableCell}
															onClick={e => e.stopPropagation()}
														>
															<Checkbox
																checked={allGroupSelected}
																indeterminate={someGroupSelected}
																disabled={groupKeys.length === 0}
																onChange={e => toggleGroup(groupKeys, e.target.checked)}
																className={classes.checkbox}
																style={{
																	color:
																		theme.palette.secondary?.value ||
																		theme.palette.secondary?.main ||
																		'#fff'
																}}
															/>
														</TableCell>
													)}
													{headCells.map(cell => (
														<TableCell
															classes={{ root: classes.tableCell }}
															sx={{ fontSize: '1.1rem' }}
															key={cell.id}
															align={cell.numeric ? 'center' : 'left'}
														>
															<CopyableCell
																value={getValue(row, cell.id, cell)}
																cellConfig={{ ...cell, row }}
																classes={classes}
																theme={theme}
															/>
														</TableCell>
													))}
												</TableRow>
												<TableRow>
													<TableCell className={classes.expandedRowCell} colSpan={colSpan}>
														<Collapse in={isExpanded} timeout="auto" unmountOnExit>
															<div className={classes.innerWrapper}>
																<TableOmniNewAnimated
																	rows={innerRows}
																	headCells={innerHeadCells}
																	rowKey={innerRowKey}
																	tableName={getInnerTitle ? true : undefined}
																	title={
																		getInnerTitle ? getInnerTitle(row) : undefined
																	}
																	checkBox={checkBox}
																	selectedRows={groupKeys.filter(k =>
																		selectedInner.includes(k)
																	)}
																	setSelectedRows={next =>
																		setGroupSelected(groupKeys, next)
																	}
																	perPageRows={innerPerPageRows}
																	rowsPerPageOptions={innerRowsPerPageOptions}
																	loading={false}
																	isLoadingPagination={false}
																	fixedHeight
																	maxTableHeight="360px"
																	emptyMessage="No campaigns"
																/>
															</div>
														</Collapse>
													</TableCell>
												</TableRow>
											</React.Fragment>
										);
									})}
								</TableBody>
							</Table>
						</div>
						<div className={classes.pagination}>
							<TablePagination
								rowsPerPageOptions={rowsPerPageOptions}
								component="div"
								count={rows.length}
								rowsPerPage={rowsPerPage === -1 ? rows.length : rowsPerPage}
								page={page}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
								ActionsComponent={PaginationActionsTableOmni}
							/>
						</div>
					</>
				)}
			</Paper>
		</Box>
	);
}

TableOmniExpandable.propTypes = {
	rows: PropTypes.array,
	headCells: PropTypes.array.isRequired,
	innerHeadCells: PropTypes.array.isRequired,
	getInnerRows: PropTypes.func,
	outerRowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	innerRowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
	checkBox: PropTypes.bool,
	selectedInner: PropTypes.array,
	setSelectedInner: PropTypes.func,
	getInnerTitle: PropTypes.func,
	loading: PropTypes.bool,
	emptyMessage: PropTypes.string,
	fixedHeight: PropTypes.bool,
	maxTableHeight: PropTypes.string
};

export default TableOmniExpandable;
